import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../components/ui/Button';
import { ApplicationOption, TipItem } from '../../types/components';

const UserApply: FC = () => {
  const applications: ApplicationOption[] = [
    {
      company: 'TechCorp',
      jobTitle: 'Senior React Developer',
      appliedDate: '2 days ago',
      status: 'Under Review',
    },
    {
      company: 'StartupXYZ',
      jobTitle: 'Frontend Engineer',
      appliedDate: '1 week ago',
      status: 'Interview Scheduled',
    },
    {
      company: 'InnovateLab',
      jobTitle: 'Full Stack Developer',
      appliedDate: '2 weeks ago',
      status: 'Not Selected',
    },
  ];

  const tips: TipItem[] = [
    {
      icon: '📝',
      title: 'Complete Profile',
      description: 'Make sure your profile is complete with all relevant information',
    },
    {
      icon: '🎯',
      title: 'Targeted Applications',
      description: 'Apply to jobs that match your skills and experience',
    },
    {
      icon: '⏰',
      title: 'Follow Up',
      description: 'Follow up on your applications and stay engaged',
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-primary-black mb-4">
          Apply for Jobs
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Choose how you'd like to apply for jobs. You can apply directly to specific positions or use our multi-step application process.
        </p>
      </div>

      {/* Application Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Direct Application */}
        <div className="bg-white rounded-2xl p-8 shadow-soft">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-lightGreen rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-primary-black mb-4">
              Browse & Apply
            </h2>
            <p className="text-gray-600 mb-6">
              Search through available job listings and apply directly to positions that interest you.
            </p>
            <Link to="/user/jobs">
              <Button className="w-full">
                Browse Jobs
              </Button>
            </Link>
          </div>
        </div>

        {/* Multi-step Application */}
        <div className="bg-white rounded-2xl p-8 shadow-soft">
          <div className="text-center">
            <div className="w-16 h-16 bg-primary-teaGreen rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-primary-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-primary-black mb-4">
              Multi-step Application
            </h2>
            <p className="text-gray-600 mb-6">
              Use our guided application process to apply for specific jobs with detailed information.
            </p>
            <Link to="/user/apply/123">
              <Button variant="outline" className="w-full">
                Start Application
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Application Status */}
      <div className="bg-white rounded-2xl p-8 shadow-soft">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-primary-black">
            Your Applications
          </h2>
          <Link to="/user/status">
            <Button variant="ghost">
              View All
            </Button>
          </Link>
        </div>

        <div className="space-y-4">
          {/* Application 1 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary-lightGreen rounded-full flex items-center justify-center">
                <span className="text-primary-black font-bold">TC</span>
              </div>
              <div>
                <h3 className="font-semibold text-primary-black">{applications[0].jobTitle}</h3>
                <p className="text-sm text-gray-600">{applications[0].company} • Applied {applications[0].appliedDate}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                {applications[0].status}
              </span>
            </div>
          </div>

          {/* Application 2 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary-teaGreen rounded-full flex items-center justify-center">
                <span className="text-primary-black font-bold">SX</span>
              </div>
              <div>
                <h3 className="font-semibold text-primary-black">{applications[1].jobTitle}</h3>
                <p className="text-sm text-gray-600">{applications[1].company} • Applied {applications[1].appliedDate}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                {applications[1].status}
              </span>
            </div>
          </div>

          {/* Application 3 */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-primary-lightGreen/20 rounded-full flex items-center justify-center">
                <span className="text-primary-lightGreen font-bold">IL</span>
              </div>
              <div>
                <h3 className="font-semibold text-primary-black">{applications[2].jobTitle}</h3>
                <p className="text-sm text-gray-600">{applications[2].company} • Applied {applications[2].appliedDate}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <span className="px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                {applications[2].status}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-gradient-to-r from-primary-lightGreen to-primary-teaGreen rounded-2xl p-8 text-primary-black">
        <h2 className="text-2xl font-bold mb-4">Application Tips</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tips.map((tip, index) => (
            <div key={index} className="text-center">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-xl">{tip.icon}</span>
              </div>
              <h3 className="font-semibold mb-2">{tip.title}</h3>
              <p className="text-sm opacity-90">
                {tip.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UserApply;
